/**
 * Synterra Labs — Cart Page
 * Live cart updates (qty +/-, remove, note), terms checkbox, urgency timer,
 * product recommendations and shipping rate calculator.
 *
 * Uses native Shopify Ajax cart endpoints:
 *   /cart/change.js, /cart/update.js, /cart.js
 *   /recommendations/products.json
 *   /cart/prepare_shipping_rates.json + /cart/async_shipping_rates.json
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-syntera-cart-page]');
  if (!root) return;

  // --------- Helpers ---------
  function $(sel, ctx) { return (ctx || root).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); }
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function formatMoney(cents) {
    var n = (cents / 100).toFixed(2);
    return n.replace('.', ',');
  }

  // --------- Cart state ---------
  function getThreshold() {
    return parseInt(root.dataset.freeShippingThreshold, 10) || 0;
  }

  // Section ID needed for Shopify's Sections API — read from the Liquid-injected
  // data-section-id (matches the key in the template's sections JSON, e.g. "main").
  var SECTION_ID = root.dataset.sectionId || 'main';

  // Track in-flight requests per cart line to prevent races
  var pendingByLine = Object.create(null);

  /**
   * Swap the cart UI in-place using the server-rendered HTML from
   * Shopify's Sections API. This is the ONLY source of truth — no
   * client-side DOM patching by key. Eliminates all matching bugs.
   */
  function applySectionHTML(html) {
    if (!html) return false;
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    var fresh = doc.querySelector('[data-syntera-cart-page]');
    if (!fresh) return false;

    // 1. Swap items list
    var newItems = fresh.querySelector('[data-syntera-cart-items]');
    var curItems = root.querySelector('[data-syntera-cart-items]');
    if (newItems && curItems) curItems.innerHTML = newItems.innerHTML;

    // 2. Swap totals row
    var newTotals = fresh.querySelector('.syntera-cart-page__totals');
    var curTotals = root.querySelector('.syntera-cart-page__totals');
    if (newTotals && curTotals) curTotals.innerHTML = newTotals.innerHTML;

    // 3. Swap shipping progress (free shipping bar + message)
    var newShip = fresh.querySelector('[data-syntera-shipping]');
    var curShip = root.querySelector('[data-syntera-shipping]');
    if (newShip && curShip) curShip.innerHTML = newShip.innerHTML;

    // 4. Sticky mobile subtotal
    $$('[data-syntera-subtotal]').forEach(function (n) {
      var freshNode = fresh.querySelector('[data-syntera-subtotal]');
      if (freshNode) n.textContent = freshNode.textContent;
    });

    // 5. If the items list is now empty, reload to show the empty state
    var freshCount = (fresh.querySelectorAll('[data-cart-key]') || []).length;
    if (freshCount === 0) {
      window.location.reload();
      return true;
    }
    return true;
  }

  /**
   * Change a line quantity. Uses Shopify's Sections API so the cart UI is
   * server-rendered — guaranteed consistent with the cart state.
   */
  function changeLine(key, quantity, lineNode) {
    // Cancel any pending request for this line
    if (pendingByLine[key]) pendingByLine[key].abort = true;
    var token = { abort: false };
    pendingByLine[key] = token;

    // Disable controls on this line during fetch
    if (lineNode) {
      lineNode.classList.add('is-updating');
      lineNode.querySelectorAll('button, input').forEach(function (el) { el.setAttribute('disabled', ''); });
    }

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, sections: SECTION_ID })
    })
    .then(function (r) { return r.json(); })
    .then(function (data) {
      if (token.abort) return; // a newer click superseded us
      delete pendingByLine[key];

      if (data && data.status && data.status >= 400) {
        console.warn('[Synterra Cart] /cart/change error:', data);
        if (lineNode) {
          lineNode.classList.remove('is-updating');
          lineNode.querySelectorAll('button, input').forEach(function (el) { el.removeAttribute('disabled'); });
        }
        return;
      }

      // Prefer server-rendered HTML; fall back to a full reload if missing
      var html = data.sections && data.sections[SECTION_ID];
      if (html) {
        applySectionHTML(html);
        // Update header cart count badge globally
        document.querySelectorAll('[data-syntera-cart-count]').forEach(function (n) {
          n.textContent = data.item_count;
          if (data.item_count > 0) n.removeAttribute('hidden');
          else n.setAttribute('hidden', '');
        });
      } else {
        window.location.reload();
        return;
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    })
    .catch(function (err) {
      console.error('[Synterra Cart] change failed:', err);
      if (lineNode) {
        lineNode.classList.remove('is-updating');
        lineNode.querySelectorAll('button, input').forEach(function (el) { el.removeAttribute('disabled'); });
      }
      delete pendingByLine[key];
    });
  }

  /** Save cart note */
  function saveNote(note) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: note })
    }).catch(function () {});
  }

  /** Update a single cart attribute (e.g. order protection toggle) */
  function setAttribute(name, value) {
    var data = { attributes: {} };
    data.attributes[name] = value;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data)
    }).catch(function () {});
  }

  // --------- Quantity controls (event delegation on root) ---------
  function setupQuantity() {
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-syntera-qty-up], [data-syntera-qty-down], [data-syntera-remove]');
      if (!btn || btn.disabled) return;
      var item = btn.closest('[data-cart-key]');
      if (!item) return;
      e.preventDefault();
      var key = item.getAttribute('data-cart-key');
      var input = item.querySelector('[data-syntera-qty-input]');
      var current = input ? parseInt(input.value, 10) || 0 : 0;
      var next;

      if (btn.hasAttribute('data-syntera-qty-up')) {
        next = current + 1;
      } else if (btn.hasAttribute('data-syntera-qty-down')) {
        next = Math.max(0, current - 1);
      } else {
        // remove
        next = 0;
      }

      // Optimistic input update so the click feels instant
      if (input && next > 0) input.value = next;
      if (next === 0) item.classList.add('is-removing');

      changeLine(key, next, item);
    });

    // Manual number input change (typing a new qty)
    root.addEventListener('change', function (e) {
      if (!e.target.matches('[data-syntera-qty-input]')) return;
      var item = e.target.closest('[data-cart-key]');
      if (!item) return;
      var key = item.getAttribute('data-cart-key');
      var qty = Math.max(0, parseInt(e.target.value, 10) || 0);
      if (qty === 0) item.classList.add('is-removing');
      changeLine(key, qty, item);
    });

    // Prevent Enter on qty input from submitting the form
    root.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target.matches('[data-syntera-qty-input]')) {
        e.preventDefault();
        e.target.blur();
      }
    });
  }

  // --------- Order note ---------
  function setupNote() {
    var note = $('[data-syntera-cart-note]');
    if (!note) return;
    var debounced = debounce(function () { saveNote(note.value); }, 600);
    note.addEventListener('input', debounced);
  }

  // --------- Order protection upsell ---------
  function setupProtection() {
    var box = $('[data-syntera-protection]');
    if (!box) return;
    box.addEventListener('change', function () {
      setAttribute('order_protection', box.checked ? 'yes' : '');
    });
  }

  // --------- Terms checkbox → checkout disabled gate ---------
  function setupTerms() {
    var checkbox = $('[data-syntera-terms]');
    if (!checkbox) return;
    var buttons = $$('[data-syntera-checkout]');
    function sync() {
      buttons.forEach(function (btn) {
        if (checkbox.checked) btn.removeAttribute('disabled');
        else btn.setAttribute('disabled', '');
      });
    }
    checkbox.addEventListener('change', sync);
    // Initial sync
    sync();
  }

  // --------- Urgency timer ---------
  function setupTimer() {
    var t = $('[data-syntera-timer]');
    if (!t) return;
    var display = t.querySelector('[data-syntera-timer-display]');
    if (!display) return;
    var minutes = parseInt(t.getAttribute('data-timer-minutes'), 10) || 10;
    var expiredText = t.getAttribute('data-timer-expired-text') || 'Reservering verlopen';

    // Persist start time in sessionStorage so reload doesn't reset
    var key = 'syntera-cart-timer-start';
    var stored = parseInt(sessionStorage.getItem(key), 10);
    var now = Date.now();
    var startedAt = (stored && stored > now - minutes * 60 * 1000) ? stored : now;
    if (!stored || startedAt !== stored) sessionStorage.setItem(key, String(startedAt));

    function tick() {
      var elapsed = Math.floor((Date.now() - startedAt) / 1000);
      var remaining = (minutes * 60) - elapsed;
      if (remaining <= 0) {
        display.textContent = '00:00';
        var textNode = t.querySelector('.syntera-cart-page__timer-text');
        if (textNode) textNode.textContent = expiredText;
        clearInterval(timerId);
        return;
      }
      var m = Math.floor(remaining / 60);
      var s = remaining % 60;
      display.textContent = (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
    }
    tick();
    var timerId = setInterval(tick, 1000);
  }

  // --------- Recommendations ---------
  function renderRec(p) {
    var img = (p.featured_image && p.featured_image.url) || p.featured_image || '';
    var imgUrl = typeof img === 'string' ? img : (img.url || '');
    var price = formatMoney(p.price);
    var variantId = (p.variants && p.variants[0] && p.variants[0].id) || '';
    var available = !!(p.available);
    // "Meest verkocht"-label: voor Metabolic Optimization (handle) én voor elk product met de tag "Meest verkocht".
    var isBestseller = (p.handle === 'metabolic-optimization') ||
      (Array.isArray(p.tags) && p.tags.some(function (t) { return String(t).toLowerCase().indexOf('meest verkocht') >= 0; }));
    // Badge in de productkleur (paars / blauw / groen / oranje), met goud als fallback.
    var REC_ACCENTS = { 'brain-focus': '#8F5FD7', 'cellular-energy': '#2F8FEA', 'longevity-complex': '#3E8B67', 'metabolic-optimization': '#C8842F' };
    var badgeColor = REC_ACCENTS[p.handle] || '#8A7547';
    // Korte omschrijving per product (tagline); valt terug op de productbeschrijving.
    var REC_TAGLINES = { 'brain-focus': 'Focus & mentale prestaties', 'cellular-energy': 'Energie op celniveau', 'longevity-complex': 'Ondersteunt gezond ouder worden', 'metabolic-optimization': 'Ondersteunt een actief metabolisme' };
    var recDesc = REC_TAGLINES[p.handle] || (p.description ? p.description.replace(/<[^>]+>/g, '').trim().slice(0, 46) : '');
    return '' +
      '<div class="syntera-cart-page__rec-card" style="border-color:' + badgeColor + ';">' +
        '<a class="syntera-cart-page__rec-head" href="' + p.url + '" style="background:' + badgeColor + ';">' + (p.title || '') + '</a>' +
        '<div class="syntera-cart-page__rec-body">' +
          (isBestseller ? '<span class="syntera-cart-page__rec-badge" style="background:' + badgeColor + ';">Meest verkocht</span>' : '') +
          '<a class="syntera-cart-page__rec-thumb" href="' + p.url + '">' +
            (imgUrl ? '<img src="' + imgUrl + '" alt="' + (p.title || '') + '" loading="lazy">' : '') +
          '</a>' +
          (recDesc ? '<p class="syntera-cart-page__rec-desc">' + recDesc + '</p>' : '') +
          '<p class="syntera-cart-page__rec-price">' + price + '</p>' +
          (available && variantId
            ? '<form action="/cart/add" method="post" enctype="multipart/form-data">' +
                '<input type="hidden" name="id" value="' + variantId + '">' +
                '<input type="hidden" name="quantity" value="1">' +
                '<button type="submit" class="syntera-cart-page__rec-add" style="background:' + badgeColor + ';">Toevoegen</button>' +
              '</form>'
            : '<a href="' + p.url + '" class="syntera-cart-page__rec-add" style="background:' + badgeColor + ';display:block;text-align:center;text-decoration:none;">Bekijken</a>') +
        '</div>' +
      '</div>';
  }

  function setupRecommendations() {
    var box = $('[data-syntera-cart-recs]');
    if (!box) return;
    if (root.dataset.enableRecommendations !== 'true') { box.remove(); return; }

    var productId = box.getAttribute('data-product-id');
    var limit = parseInt(box.getAttribute('data-limit'), 10) || 4;
    var track = box.querySelector('[data-syntera-cart-recs-track]');
    if (!productId || !track) return;

    var url = '/recommendations/products.json?product_id=' + encodeURIComponent(productId)
            + '&limit=' + limit
            + '&intent=related';
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.products || !data.products.length) {
          box.remove();
          return;
        }
        track.innerHTML = data.products.map(renderRec).join('');
      })
      .catch(function () { box.remove(); });
  }

  // --------- Shipping calculator ---------
  function pollShippingRates(callback, attempts) {
    attempts = attempts || 0;
    fetch('/cart/async_shipping_rates.json', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (Array.isArray(data.shipping_rates)) {
          callback(null, data.shipping_rates);
        } else if (attempts < 10) {
          setTimeout(function () { pollShippingRates(callback, attempts + 1); }, 600);
        } else {
          callback(new Error('Timeout'));
        }
      })
      .catch(function (err) { callback(err); });
  }

  function setupShippingCalc() {
    var btn = $('[data-syntera-ship-calculate]');
    if (!btn) return;
    if (root.dataset.enableShippingCalc !== 'true') return;

    btn.addEventListener('click', function () {
      var country = ($('[data-syntera-ship-country]') || {}).value || 'NL';
      var zip = (($('[data-syntera-ship-zip]') || {}).value || '').trim();
      var rates = $('[data-syntera-ship-rates]');
      if (!rates) return;
      rates.removeAttribute('hidden');
      rates.innerHTML = '<p class="syntera-cart-page__ship-error">Tarieven worden berekend…</p>';
      btn.disabled = true;

      var body = new URLSearchParams();
      body.append('shipping_address[country]', country);
      body.append('shipping_address[zip]', zip);

      fetch('/cart/prepare_shipping_rates.json', { method: 'POST', body: body })
        .then(function (r) { return r.text(); })
        .then(function () {
          pollShippingRates(function (err, list) {
            btn.disabled = false;
            if (err || !list || !list.length) {
              rates.innerHTML = '<p class="syntera-cart-page__ship-error">Geen tarieven beschikbaar — exacte verzendkosten zie je bij afrekenen.</p>';
              return;
            }
            rates.innerHTML = list.map(function (rate) {
              var price = formatMoney(parseFloat(rate.price) * 100);
              return '<div class="syntera-cart-page__ship-rate">' +
                       '<span class="syntera-cart-page__ship-rate-name">' + rate.name + '</span>' +
                       '<span class="syntera-cart-page__ship-rate-price">' + price + '</span>' +
                     '</div>';
            }).join('');
          });
        })
        .catch(function () {
          btn.disabled = false;
          rates.innerHTML = '<p class="syntera-cart-page__ship-error">Kon tarieven niet ophalen. Probeer het later opnieuw.</p>';
        });
    });
  }

  // --------- Init ---------
  function init() {
    setupQuantity();
    setupNote();
    setupProtection();
    setupTerms();
    setupTimer();
    setupRecommendations();
    setupShippingCalc();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
